import process from "node:process";

const WAKELOOP_CLI_COMMAND = "wakeloop";
const WAKELOOP_CLI_NPX_COMMAND = "npx -y wakeloop@latest";
const WAKELOOP_SETUP_FOLLOW_UP_CLI_COMMAND_ENV =
  "WAKELOOP_SETUP_FOLLOW_UP_CLI_COMMAND";
const WAKELOOP_USER_FACING_CLI_COMMAND_ENV = "WAKELOOP_USER_FACING_CLI_COMMAND";
const WAKELOOP_TOP_LEVEL_COMMANDS = [
  "adapter",
  "agents",
  "auth",
  "computer",
  "daemon",
  "doctor",
  "help",
  "login",
  "profile",
  "profiles",
  "prompt",
  "reset",
  "send",
  "service",
  "setup",
  "skills",
  "space",
  "start",
  "tail",
  "upgrade",
  "view",
  "whoami",
] as const;
const WAKELOOP_COMMAND_PREFIX_RE = /^wakeloop(?=$|\s)/u;
const WAKELOOP_COMMAND_LINE_RE =
  /^(?<prefix>\s*(?:(?:[$*-])\s+|\d+\)\s+)??)(?<command>wakeloop(?=$|\s).*)$/u;
const WAKELOOP_COMMAND_AFTER_PIPE_RE =
  /^(?<prefix>.*\|\s+)(?<command>wakeloop(?=$|\s).*)$/u;
const INLINE_WAKELOOP_COMMAND_RE = /`(wakeloop(?: [^`]*)?)`/gu;
const CLI_OPTION_NAME_RE = /^[a-z][a-z0-9-]*$/u;
const FIRST_CLI_OPERAND_OR_OPTION_RE =
  /^(?<commandPath>.*?)(?<tail>\s+(?:--|<).*)$/u;
const INLINE_WAKELOOP_TOP_LEVEL_MENTION_RE = new RegExp(
  `\\bats (?<command>${WAKELOOP_TOP_LEVEL_COMMANDS.join("|")})\\b`,
  "gu"
);
const NEWLINE_SPLIT_RE = /\r?\n/u;
const WHITESPACE_SPLIT_RE = /\s+/u;
const PROFILE_AWARE_TOP_LEVEL_COMMANDS = new Set(["whoami"]);
const PROFILE_AWARE_SPACE_COMMANDS = new Set([
  "add-me",
  "add-agents",
  "add-human-profiles",
  "add-members",
  "contract",
  "create",
  "delete",
  "guide",
  "history",
  "join",
  "leave",
  "list",
  "password",
  "publication",
  "remove-members",
  "result",
  "send",
  "status",
  "updates",
  "watch",
]);
const SPACE_PARENT_OPTIONS_WITH_VALUE = new Set(["--gateway-url"]);

type CliEntryCommandInput = {
  env?: NodeJS.ProcessEnv;
  argv?: string[];
};

type ProfileAwareCliCommandInput = CliEntryCommandInput & {
  profileId?: string | null;
  resolvedView?: "human" | "agent" | string;
};

export function resolveUserFacingCliEntryCommand(
  input: CliEntryCommandInput = {}
): string {
  const env = input.env ?? process.env;
  const explicitCommand = normalizeText(
    env[WAKELOOP_USER_FACING_CLI_COMMAND_ENV]
  );
  if (explicitCommand.length > 0) {
    return explicitCommand;
  }
  return isNpxCliInvocation(input)
    ? WAKELOOP_CLI_NPX_COMMAND
    : WAKELOOP_CLI_COMMAND;
}

export function resolveSetupFollowUpCliEntryCommand(
  input: CliEntryCommandInput = {}
): string {
  const env = input.env ?? process.env;
  const setupCommand = normalizeText(
    env[WAKELOOP_SETUP_FOLLOW_UP_CLI_COMMAND_ENV]
  );
  if (setupCommand.length > 0) {
    return setupCommand;
  }
  return resolveUserFacingCliEntryCommand(input);
}

export function formatWakeLoopCliCommand(
  command: string,
  input: CliEntryCommandInput = {}
): string {
  if (!WAKELOOP_COMMAND_PREFIX_RE.test(command)) {
    return command;
  }
  return command.replace(
    WAKELOOP_COMMAND_PREFIX_RE,
    resolveUserFacingCliEntryCommand(input)
  );
}

export function formatWakeLoopCliCommands(
  commands: string[],
  input: CliEntryCommandInput = {}
): string[] {
  return commands.map((command) => formatWakeLoopCliLine(command, input));
}

export function formatProfileAwareWakeLoopCliCommand(
  command: string,
  input: ProfileAwareCliCommandInput = {}
): string {
  return formatWakeLoopCliCommand(
    applyProfileAwareCommandScope(command, input),
    input
  );
}

export function formatProfileAwareWakeLoopCliCommands(
  commands: string[],
  input: ProfileAwareCliCommandInput = {}
): string[] {
  return commands.map((command) =>
    formatProfileAwareWakeLoopCliCommand(command, input)
  );
}

export function formatInlineWakeLoopCliCommands(
  text: string,
  input: CliEntryCommandInput = {}
): string {
  return text.replace(INLINE_WAKELOOP_COMMAND_RE, (_, command: string) => {
    return `\`${formatWakeLoopCliCommand(command, input)}\``;
  });
}

export function formatWakeLoopCliLine(
  text: string,
  input: CliEntryCommandInput = {}
): string {
  const inlineFormatted = formatInlineWakeLoopCliCommands(text, input);
  const lineMatch = WAKELOOP_COMMAND_LINE_RE.exec(inlineFormatted);
  if (lineMatch?.groups?.command) {
    return formatBareWakeLoopCliMentions(
      `${lineMatch.groups.prefix ?? ""}${formatWakeLoopCliCommand(
        lineMatch.groups.command,
        input
      )}`,
      input
    );
  }

  const pipeMatch = WAKELOOP_COMMAND_AFTER_PIPE_RE.exec(inlineFormatted);
  if (pipeMatch?.groups?.command) {
    return formatBareWakeLoopCliMentions(
      `${pipeMatch.groups.prefix}${formatWakeLoopCliCommand(
        pipeMatch.groups.command,
        input
      )}`,
      input
    );
  }

  return formatBareWakeLoopCliMentions(inlineFormatted, input);
}

export function formatWakeLoopCliTextBlock(
  text: string,
  input: CliEntryCommandInput = {}
): string {
  return text
    .split(NEWLINE_SPLIT_RE)
    .map((line) => formatWakeLoopCliLine(line, input))
    .join("\n");
}

function isNpxCliInvocation(input: CliEntryCommandInput): boolean {
  const env = input.env ?? process.env;
  const argv = input.argv ?? process.argv;
  const npmCommand = normalizeText(env.npm_command).toLowerCase();
  const npmExecPath = normalizePath(env.npm_execpath);
  const npmUserAgent = normalizeText(env.npm_config_user_agent).toLowerCase();
  const usesNpmExec =
    npmCommand === "exec" &&
    (npmUserAgent.startsWith("npm/") ||
      npmExecPath.includes("/npm/") ||
      npmExecPath.endsWith("/npm-cli.js"));
  if (usesNpmExec) {
    return true;
  }

  const scriptPath = normalizePath(argv[1]);
  return scriptPath.includes("/.npm/") && scriptPath.includes("_npx");
}

function formatBareWakeLoopCliMentions(
  text: string,
  input: CliEntryCommandInput = {}
): string {
  return text.replace(
    INLINE_WAKELOOP_TOP_LEVEL_MENTION_RE,
    (_, command: string) => {
      return formatWakeLoopCliCommand(`wakeloop ${command}`, input);
    }
  );
}

function applyProfileAwareCommandScope(
  command: string,
  input: ProfileAwareCliCommandInput
): string {
  if (!WAKELOOP_COMMAND_PREFIX_RE.test(command)) {
    return command;
  }

  let scopedCommand = command.trimEnd();
  const profileId = normalizeText(input.profileId ?? undefined);
  if (profileId.length > 0) {
    scopedCommand = appendCliOption(scopedCommand, "profile", profileId);
  }
  if (input.resolvedView === "agent") {
    scopedCommand = appendCliOption(scopedCommand, "view", "agent");
  }
  return scopedCommand;
}

function appendCliOption(
  command: string,
  optionName: string,
  value: string
): string {
  if (
    !CLI_OPTION_NAME_RE.test(optionName) ||
    hasCliOption(command, optionName)
  ) {
    return command;
  }
  if (optionName === "profile") {
    return insertCliOptionBeforeFirstOperandOrOption(
      command,
      optionName,
      value
    );
  }
  return `${command} --${optionName} ${value}`;
}

function insertCliOptionBeforeFirstOperandOrOption(
  command: string,
  optionName: string,
  value: string
): string {
  if (optionName !== "profile") {
    const match = FIRST_CLI_OPERAND_OR_OPTION_RE.exec(command);
    if (!(match?.groups?.commandPath && match.groups.tail)) {
      return `${command} --${optionName} ${value}`;
    }
    return `${match.groups.commandPath} --${optionName} ${value}${match.groups.tail}`;
  }

  const tokens = command.trim().split(WHITESPACE_SPLIT_RE);
  const insertionIndex = resolveProfileOptionInsertionIndex(tokens);
  if (insertionIndex === null) {
    return command;
  }
  return [
    ...tokens.slice(0, insertionIndex),
    `--${optionName}`,
    value,
    ...tokens.slice(insertionIndex),
  ].join(" ");
}

function hasCliOption(command: string, optionName: string): boolean {
  const optionPattern = new RegExp(`(?:^|\\s)--${optionName}(?:=|\\s|$)`, "u");
  return optionPattern.test(command);
}

function resolveProfileOptionInsertionIndex(tokens: string[]): number | null {
  if (tokens[0] !== WAKELOOP_CLI_COMMAND) {
    return null;
  }
  const topLevelCommand = tokens[1] ?? "";
  if (PROFILE_AWARE_TOP_LEVEL_COMMANDS.has(topLevelCommand)) {
    return 2;
  }
  if (topLevelCommand !== "space") {
    return null;
  }

  let index = 2;
  while (index < tokens.length) {
    const token = tokens[index] ?? "";
    if (SPACE_PARENT_OPTIONS_WITH_VALUE.has(token)) {
      index += 2;
      continue;
    }
    if (
      [...SPACE_PARENT_OPTIONS_WITH_VALUE].some((option) =>
        token.startsWith(`${option}=`)
      )
    ) {
      index += 1;
      continue;
    }
    break;
  }

  const spaceCommand = tokens[index] ?? "";
  if (!PROFILE_AWARE_SPACE_COMMANDS.has(spaceCommand)) {
    return null;
  }
  return index + 1;
}

function normalizePath(value: string | undefined): string {
  return normalizeText(value).replaceAll("\\", "/").toLowerCase();
}

function normalizeText(value: string | undefined): string {
  return String(value ?? "").trim();
}
